import { queryStrings } from "@/constants/query-strings";
import { getQuerystringParameter } from "@/helpers/querystring-helper";
import { saveSession } from "@/helpers/heritage-integration/order-helper.js";
import { storeActions } from "@/constants/store-actions.js";
import experimentMixin from "@/mixins/experiment-mixin";
import { experimentSettings } from "@/constants/experiments";
import { includesWindshieldReplacement } from "@/helpers/damage-helper";
import { applicationConfig } from "@/constants/application-config.js";
import store from "@/store";
import router from "@/router";
import { routeData } from "@/router/constants/routes";
import { debugLog } from "@/helpers/debug-log-helper";
import {
    isClaimAndCoverageExperimentActive,
    isNonClaimAndCoverageFlowEnabled,
    isNonClaimAndCoverageTestParentAccount,
} from "@/helpers/experiment-helper";

/*
    Used to navigate to the heritage funnel with the correct query string and url.
*/
export async function navigateToHeritageFunnel({ shouldSaveSession, pageNameToLog, navType }) {
    const parentAccountNumber = store.getters.order?.payment?.parentAccountNumber;
    const experimentList = store.getters.applicationUser.experiments;

    // C&C experiment active for this parent account number - never send to heritage, send to
    // vue app Policy Info page instead.
    if (isClaimAndCoverageExperimentActive(parentAccountNumber, experimentList)) {
        const isVerified = store.getters.order?.payment?.insuranceCoverage?.isVerified;
        const isItac = store.getters.order?.policy?.isItac;
        const isNoComp = store.getters.order?.policy?.isNoComp;

        // Coverage status is already known (verified, ITAC, or NoComp) - skip Policy Info and
        // send straight to Coverage Statement instead.
        if (isVerified || isItac || isNoComp) {
            debugLog(
                `--- ${parentAccountNumber} C&C experiment active and coverage status is verified/itac/nocomp, redirecting to coverage-statement instead of Heritage ---`,
                "",
                true
            );
            router.push({
                name: routeData.COVERAGE_STATEMENT.name,
            });
            return;
        }

        debugLog(
            `--- ${parentAccountNumber} C&C experiment active, redirecting to policy-info instead of Heritage ---`,
            "",
            true
        );
        router.push({
            name: routeData.POLICY_INFO.name,
        });
        return;
    }

    if (
        !store.getters.payment.isClaimAndCoverage &&
        (isNonClaimAndCoverageFlowEnabled(experimentList) ||
            isNonClaimAndCoverageTestParentAccount(parentAccountNumber, experimentList))
    ) {
        debugLog(
            `--- ${parentAccountNumber} Non C&C flow activeinsurance-details instead of Heritage ---`,
            "",
            true
        );
        router.push({
            name: routeData.INSURANCE_DETAILS.name,
        });
        return;
    }

    debugLog("--- Navigate to heritage start ---");
    debugLog("applicationConfig:", applicationConfig);
    debugLog("navigateToHeritageFunnel:", applicationConfig.HERITAGE_FUNNEL);
    debugLog("--- Navigate to heritage end ---");

    // Create the order (or save existing order) when navigating to Heritage Funnel.
    if (shouldSaveSession) {
        await saveSession({ pageNameToLog: pageNameToLog, shouldAwaitSaveSessionQueue: true });
    }

    var heritageParms = {
        corid: store.getters.order.referralCorrelationId,
        src: "concept-funnel",
        conceptsqid: store.getters.applicationUser.savedSessionId ?? "",
        isInsurance: store.getters.payment.isInsurance,
        vinRequired: store.getters.vehicle.vinRequired,
    };

    const promo = getQuerystringParameter(queryStrings.PROMO);

    if (promo) {
        heritageParms["promo"] = promo;
    }

    if (navType) {
        heritageParms["navType"] = navType;
    }

    if (
        (applicationConfig.HERITAGE_FUNNEL.includes("fixmyglasstest") &&
            applicationConfig.CURRENT_ENVIRONMENT === "Localhost") ||
        (applicationConfig.HERITAGE_FUNNEL.includes("fixmyglassdev") &&
            applicationConfig.CURRENT_ENVIRONMENT === "Localhost") ||
        (applicationConfig.HERITAGE_FUNNEL.includes("localhost") &&
            applicationConfig.CURRENT_ENVIRONMENT === "Localhost")
    ) {
        heritageParms["forceEndToEndInsurance"] = true;
    }

    // clear the heritage session that may still exist after an order was already submitted
    if (window.sessionStorage.getItem("createNewSessionForHeritage") !== null) {
        heritageParms["cns"] = "all";
        window.sessionStorage.removeItem("createNewSessionForHeritage");
    }

    router.navigateToExternalUrl(applicationConfig.HERITAGE_FUNNEL, heritageParms);
}

export async function skipVinLookup() {
    if (store.getters.damage.isRepair) {
        return true;
    }
    if (store.getters.vehicle.vinRequired) {
        return false;
    }
    const isVinOptionalVehicle = store.getters.order.vehicle.make
        ? await store.dispatch(storeActions.IS_VIN_OPTIONAL_VEHICLE)
        : false;

    return (
        isVinOptionalVehicle ||
        !includesWindshieldReplacement() ||
        experimentMixin.methods.hasSettingEqualTo(experimentSettings.SUPPRESS_VIN_CAPTURE, "true")
    );
}

export async function skipVinLookupNotRepair() {
    if (store.getters.damage.isRepair) {
        return false;
    }

    const isVinOptionalVehicle = store.getters.order.vehicle.make
        ? await store.dispatch(storeActions.IS_VIN_OPTIONAL_VEHICLE)
        : false;

    return (
        isVinOptionalVehicle ||
        !includesWindshieldReplacement() ||
        experimentMixin.methods.hasSettingEqualTo(experimentSettings.SUPPRESS_VIN_CAPTURE, "true")
    );
}
